import { toast } from 'react-toastify'
import { getDate, Message } from '../../../_metronic/helpers'
import { getLeavePoliciesById, syncLeavePolicyToUser } from '../../../api/ConfigurationAPI'
import { API_BASE_URL } from '../../../utils/contants'
import {
  APIResponse,
  InputErrorType,
  SubmitAPIStatusType,
  TableHeaderDataType,
} from '../../types/OtherTypes'
import { LeavePolicyInput } from '../../types/SettingModuleTypes'
import API from '../../../api/APIUrl'
import { updateLeaveBalancesOnPolicyChange } from '../../../api/LeaveAPI'
import { setupAxios } from '../../modules/auth'
import { LeavePolicyUIService } from '../../services/LeavePolicyUIService'

export const validateForm = (formField: LeavePolicyInput, leaveTypeName?: string) => {
  let cnt: number = 0
  const error: InputErrorType = {}

  // Basic required field validation
  if (!formField.leaveTypeId.trim()) {
    cnt = cnt + 1
    error.leaveTypeId = 'Leave Type is Required'
    return { error, cnt } // Can't proceed without leave type
  }

  // Get leave type name for validation - this should be passed from the component
  const selectedLeaveTypeName = leaveTypeName || ''

  if (!selectedLeaveTypeName) {
    cnt = cnt + 1
    error.leaveTypeId = 'Leave Type Name is Required for validation'
    return { error, cnt }
  }

  // Use LeavePolicyUIService for comprehensive validation
  const policyValidation = LeavePolicyUIService.validateCompletePolicy(selectedLeaveTypeName, formField)

  if (!policyValidation.isValid) {
    // Convert policy validation errors to form errors
    policyValidation.errors.forEach(errorMsg => {
      cnt = cnt + 1
      const [fieldName, message] = errorMsg.split(': ')

      // Map field names to form field names
      switch (fieldName) {
        case 'totalPerYear':
          error.totalPerYear = message || errorMsg
          break
        case 'maxDaysPerRequest':
          error.maxDaysPerRequest = message || errorMsg
          break
        case 'minAdvanceNotice':
          error.minAdvanceNotice = message || errorMsg
          break
        case 'allowEmergency':
          error.allowEmergency = message || errorMsg
          break
        case 'autoApprove':
          error.autoApprove = message || errorMsg
          break
        case 'requiresApproval':
          error.requiresApproval = message || errorMsg
          break
        case 'isPaid':
          error.isPaid = message || errorMsg
          break
        case 'isProRated':
          error.isProRated = message || errorMsg
          break
        case 'allowCarry':
          error.allowCarry = message || errorMsg
          break
        case 'carryLimit':
          error.carryLimit = message || errorMsg
          break
        case 'proRataType':
          error.proRataType = message || errorMsg
          break
        case 'proRataApplicableLeaves':
          error.proRataApplicableLeaves = message || errorMsg
          break
        case 'proRataCalculationRules':
          error.proRataCalculationRules = message || errorMsg
          break
        default:
          // For general policy errors, add to totalPerYear field as it's always visible
          if (!error.totalPerYear) {
            error.totalPerYear = errorMsg
          }
          break
      }
    })
  }

  // Additional field-specific UI validations
  const uiConfig = LeavePolicyUIService.getUIConfigForLeaveType(selectedLeaveTypeName)

  // Validate each visible field
  Object.keys(formField).forEach(fieldKey => {
    if (fieldKey in uiConfig) {
      const fieldConfig = uiConfig[fieldKey as keyof typeof uiConfig]
      const fieldValue = formField[fieldKey as keyof typeof formField]

      if (fieldConfig.visible) {
        const validation = LeavePolicyUIService.validateFieldValue(
          selectedLeaveTypeName,
          fieldKey as keyof typeof uiConfig,
          fieldValue
        )

        if (!validation.isValid && validation.message) {
          cnt = cnt + 1
          if (!error[fieldKey]) { // Don't overwrite existing errors
            error[fieldKey] = validation.message
          }
        }
      }
    }
  })

  // Specific validations for carry forward logic
  if (formField.allowCarry === 'true' && uiConfig.carryLimit.visible) {
    if (!formField.carryLimit || formField.carryLimit === '0') {
      cnt = cnt + 1
      error.carryLimit = 'Carry Limit is required when carry forward is enabled'
    }
  }

  // Specific validations for pro-rata configuration
  if (formField.isProRated === 'true') {
    // Validation suppressed because Pro-rata configuration fields are currently hidden in the UI
    // if (!formField.proRataType || formField.proRataType.trim() === '') {
    //   cnt = cnt + 1
    //   error.proRataType = 'Proration Type is required when pro-rata is enabled'
    // }
    // proRataApplicableLeaves and proRataCalculationRules are optional but can be validated if needed
  }

  return { error, cnt }
}

export const getLeavePolicyTableHeaderData = (isActionColumnNeeded: boolean) => {
  const leavePolicyTableHeaderData: TableHeaderDataType[] = [
    {
      th: {
        id: 'leaveType',
        style: {
          minWidth: '150px',
          width: '150px',
        },
      },
      text: 'Leave Type',
      isSortable: false,
    },
    {
      th: {
        id: 'totalPerYear',
        style: {
          minWidth: '90px',
          width: '90px',
        },
      },
      text: 'Total/Year',
      isSortable: false,
    },
    {
      th: {
        id: 'maxDaysPerRequest',
        style: {
          minWidth: '90px',
          width: '90px',
        },
      },
      text: 'Max/Request',
      isSortable: false,
    },
    {
      th: {
        id: 'minAdvanceNotice',
        style: {
          minWidth: '90px',
          width: '90px',
        },
      },
      text: 'Notice (hrs)',
      isSortable: false,
    },
    {
      th: {
        id: 'allowBackdated',
        style: {
          minWidth: '80px',
          width: '80px',
        },
      },
      text: 'Backdated',
      isSortable: false,
    },
    {
      th: {
        id: 'allowEmergency',
        style: {
          minWidth: '80px',
          width: '80px',
        },
      },
      text: 'Emergency',
      isSortable: false,
    },
    {
      th: {
        id: 'isPaid',
        style: {
          minWidth: '70px',
          width: '70px',
        },
      },
      text: 'Paid',
      isSortable: false,
    },
    {
      th: {
        id: 'isProRated',
        style: {
          minWidth: '80px',
          width: '80px',
        },
      },
      text: 'Pro-Rated',
      isSortable: false,
    },
    {
      th: {
        id: 'allowCarry',
        style: {
          minWidth: '80px',
          width: '80px',
        },
      },
      text: 'Carry Fwd',
      isSortable: false,
    },
    {
      th: {
        id: 'carryLimit',
        style: {
          minWidth: '80px',
          width: '80px',
        },
      },
      text: 'Carry Limit',
      isSortable: false,
    },
    {
      th: {
        id: 'actions',
        style: {
          width: '50px',
          minWidth: '50px',
        },
        hidden: isActionColumnNeeded,
      },
      text: 'Actions',
      justifyContent: 'end',
      isSortable: false,
    },
  ]
  return { leavePolicyTableHeaderData }
}

export const getLeavePolicyByID = (
  id: string,
  formField: LeavePolicyInput,
  setFormField: Function,
  setEditId: Function,
  setShowForm: Function,
  setInputError: Function,
  setSelectedLeaveTypeName?: Function,
  setSelectedApplicableLeaves?: Function
) => {
  getLeavePoliciesById(id).then((res: APIResponse) => {
    if (res.statusCode === 200) {
      // First set the form fields with all data, ensuring proper conversion
      const updatedFormField = {
        ...formField,
        leaveTypeId: res?.data?.leaveTypeId ?? '',
        totalPerYear: res?.data?.totalPerYear ? String(res.data.totalPerYear) : '',
        maxDaysPerRequest: res?.data?.maxDaysPerRequest ? String(res.data.maxDaysPerRequest) : '',
        minAdvanceNotice: res?.data?.minAdvanceNotice ? String(res.data.minAdvanceNotice) : '',
        allowBackdated: res?.data?.allowBackdated !== undefined ? String(res.data.allowBackdated) : '',
        allowEmergency: res?.data?.allowEmergency !== undefined ? String(res.data.allowEmergency) : '',
        autoApprove: res?.data?.autoApprove !== undefined ? String(res.data.autoApprove) : '',
        requiresApproval: res?.data?.requiresApproval !== undefined ? String(res.data.requiresApproval) : '',
        isPaid: res?.data?.isPaid !== undefined ? String(res.data.isPaid) : '',
        isProRated: res?.data?.isProRated !== undefined ? String(res.data.isProRated) : '',
        proRataType: res?.data?.proRataType || '',
        proRataApplicableLeaves: res?.data?.proRataApplicableLeaves || '',
        proRataCalculationRules: res?.data?.proRataCalculationRules || '',
        allowCarry: res?.data?.allowCarry !== undefined ? String(res.data.allowCarry) : '',
        carryLimit: res?.data?.carryLimit ? String(res.data.carryLimit) : '',
        creditFrequency: res?.data?.creditFrequency || 'ANNUAL',
        maxBalance: res?.data?.maxBalance ? String(res.data.maxBalance) : '',
        encashable: res?.data?.encashable !== undefined ? String(res.data.encashable) : 'false',
      }

      // Set the leave type name for UI configuration if callback is provided
      const leaveTypeName = res?.data?.leaveType?.name || ''
      if (setSelectedLeaveTypeName && leaveTypeName) {
        setSelectedLeaveTypeName(leaveTypeName)
      }

      // Set selected applicable leaves if callback is provided
      if (setSelectedApplicableLeaves && updatedFormField.proRataApplicableLeaves) {
        const applicableLeaves = updatedFormField.proRataApplicableLeaves
          .split(',')
          .map((leave: string) => leave.trim())
          .filter((leave: string) => leave.length > 0)
        setSelectedApplicableLeaves(applicableLeaves)
      }

      // Apply UI defaults while preserving existing values - this ensures proper form rendering
      const finalFormField = leaveTypeName
        ? LeavePolicyUIService.applyUIDefaults(leaveTypeName, updatedFormField)
        : updatedFormField

      setFormField(finalFormField)
      setEditId(id)
      setInputError({})
      setShowForm(true)
    } else {
      Message(res.message, 'error')
    }
  }).catch((error) => {
    console.error('Error fetching leave policy:', error)
    Message('Failed to load leave policy data', 'error')
  })
}

export const syncLeavePolicyUsers = async (setSubmitAPIStatus: Function) => {
  let { token, id } = setupAxios()
  setSubmitAPIStatus((prev: SubmitAPIStatusType) => ({ ...prev, loading: true }))


  try {
    // First, try the new dynamic balance update approach
    const dynamicUpdateRes = await updateLeaveBalancesOnPolicyChange();

    if (dynamicUpdateRes.statusCode === 200) {
      toast.success(
        `Successfully updated ${dynamicUpdateRes.data.totalUpdated} leave balance entries for ${dynamicUpdateRes.data.affectedUsers} users`
      );
      setSubmitAPIStatus((prev: SubmitAPIStatusType) => ({ ...prev, loading: false }));
      return;
    } else {
      console.warn('Dynamic update failed, falling back to legacy sync:', dynamicUpdateRes.message);
    }
  } catch (error) {
    console.error('Dynamic update error, falling back to legacy sync:', error);
  }

  // Fallback to legacy sync method
  fetch(`${API_BASE_URL}/leave-balance/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }).then(async (response) => {
    let res = await response?.json()
    if (res?.statusCode === 200) {
      toast.success(res?.message || 'Leave balances updated successfully')
    } else {
      console.error('Legacy sync failed:', res?.message);
      Message(res?.message || 'Failed to update leave balances', 'error')
    }
    setSubmitAPIStatus((prev: SubmitAPIStatusType) => ({ ...prev, loading: false }))
  }).catch((error) => {
    console.error('Network error during legacy sync:', error);
    Message('Network error. Please try again.', 'error')
    setSubmitAPIStatus((prev: SubmitAPIStatusType) => ({ ...prev, loading: false }))
  })
}
